import api from './base/BaseApi';
import { handleApiError } from './base/handleApiError';
import {
  GetCardResponse,
  GetCardsRequest,
  RqAddCardSpendLimit,
  RqCreateCards,
  RqSetCardRule,
  RsBatchCreateCard,
  RsCardRule,
  RsCardSpendLimit,
  SpendLimitTypeEnum
} from './types/Card';
import { EnumValues } from './types/EnumValues';

const getCardsRequestParam = (options: GetCardsRequest) => {
  const urlParams = new URLSearchParams();
  if (options.accountId) {
    urlParams.append('accountId', options.accountId);
  }

  if (options.businessId) {
    urlParams.append('businessId', options.businessId);
  }

  if (options.walletId) {
    urlParams.append('walletId', options.walletId);
  }

  if (options.customerId) {
    urlParams.append('customerId', options.customerId);
  }

  if (options.ownedType) {
    urlParams.append('ownedType', options.ownedType);
  }

  return urlParams.toString();
};

export const getCards = async (options: GetCardsRequest) =>
  api
    .get<GetCardResponse[]>(`api/v1/cards?${getCardsRequestParam(options)}`)
    .then((response) => response)
    .catch(handleApiError);

export const getCardRules = async (cardId: string) =>
  api
    .get<RsCardRule[]>(`api/v1/cards/${cardId}/rules`)
    .then((response) => response)
    .catch(handleApiError);

export const setCardRule = async (cardId: string, rule: RqSetCardRule) =>
  api
    .put<RsCardRule, RqSetCardRule>(`api/v1/cards/${cardId}/rules`, rule)
    .then((response) => response)
    .catch(handleApiError);

// Only ever returns entries that have actually been set - see RsCardSpendLimit's own comment.
export const getCardLimits = async (cardId: string) =>
  api
    .get<RsCardSpendLimit[]>(`api/v1/cards/${cardId}/limits`)
    .then((response) => response)
    .catch(handleApiError);

// Upserts exactly one limit type per call - there's no "replace all limits at once" endpoint.
export const setCardSpendLimit = async (cardId: string, limit: RqAddCardSpendLimit) =>
  api
    .put<RsCardSpendLimit, RqAddCardSpendLimit>(`api/v1/cards/${cardId}/limits`, limit)
    .then((response) => response)
    .catch(handleApiError);

// Omit limitType to clear every limit on the card in one call.
export const deleteCardSpendLimit = async (
  cardId: string,
  limitType?: EnumValues<typeof SpendLimitTypeEnum>
) =>
  api
    .delete<unknown, undefined>(
      `api/v1/cards/${cardId}/limits${limitType ? `?limitType=${limitType}` : ''}`
    )
    .then((response) => response)
    .catch(handleApiError);

export const deleteCard = async (cardId: string) =>
  api
    .delete<unknown, undefined>(`api/v1/cards/${cardId}`)
    .then((response) => response)
    .catch(handleApiError);

// v2, not v1 - see EmergeFrontService's CardV2Controller/BatchCreateCardsUseCase. Named "Cards"
// (plural, takes a set of customerIds) since it's the same batch endpoint mobile uses to create
// one card each for one or more team members in a single call - here always called with exactly
// one customerId.
export const createCards = async (payload: RqCreateCards) =>
  api
    .post<RsBatchCreateCard, RqCreateCards>('api/v2/cards', payload)
    .then((response) => response)
    .catch(handleApiError);
